import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDateShort, formatTime } from "@/lib/format";
import {
  PARTNER_TYPES, PARTNER_STATUS, COMMISSION_STATUS, PARTNER_ACTION_LABELS, commissionRuleText,
} from "@/lib/partnerConstants";
import { BOOKING_STATUS, PAYMENT_METHODS } from "@/lib/bookingConstants";
import { PartnerDialog, DeactivateDialog } from "@/components/partners/PartnerDialogs";
import { SettlementDialog, SettlementDetailDialog } from "@/components/partners/SettlementDialogs";
import { ChevronLeft, Loader2, Pencil, Power, Phone, Mail, User, Wallet } from "lucide-react";

const TABS = [["overview", "Aperçu"], ["bookings", "Réservations"], ["commissions", "Commissions"], ["settlements", "Règlements"], ["history", "Historique"]];

export default function PartenaireDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { orgId, can } = useAuth();
  const [tab, setTab] = useState("overview");
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const [detailSettlement, setDetailSettlement] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["partner-detail", orgId, id],
    queryFn: async () => (await api.get(`/api/partners/${id}`, { params: { organization_id: orgId } })).data,
    enabled: !!orgId && !!id,
  });

  if (isLoading || !data) return <div className="flex h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  const p = data.partner;
  const currency = data.currency;
  const s = data.summary;
  const st = PARTNER_STATUS[p.status] || PARTNER_STATUS.ACTIVE;
  const m = (c) => formatMoney(c || 0, currency);
  const dueBookings = data.bookings.filter((b) => b.commission_status === "DUE" && b.commission_remaining_cents > 0);

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="partenaire-detail-page">
      <button onClick={() => nav("/partenaires")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft className="h-4 w-4" /> Partenaires</button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3"><h1 className="text-2xl font-bold text-slate-900">{p.name}</h1><span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${st.cls}`} data-testid="partner-detail-status">{st.label}</span></div>
          <p className="text-slate-500"><span className="font-mono">{p.partner_reference}</span> · {PARTNER_TYPES[p.partner_type]} · Commission {commissionRuleText(p.default_commission_type, p.default_commission_value, currency)}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {can("partners.settle") && s.due_cents > 0 && (
            <button onClick={() => setSettleOpen(true)} data-testid="new-settlement-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Wallet className="h-4 w-4" /> Régler une commission</button>
          )}
          {can("partners.manage") && (<>
            <button onClick={() => setEditOpen(true)} data-testid="edit-partner-button" className="flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"><Pencil className="h-4 w-4" /> Modifier</button>
            <button onClick={() => setStatusOpen(true)} data-testid="toggle-partner-status-button" className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"><Power className="h-4 w-4" /> {p.status === "ACTIVE" ? "Désactiver" : "Réactiver"}</button>
          </>)}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi label="Réservations" value={s.bookings_count} testid="partner-detail-bookings" />
        <Kpi label="À régler" value={m(s.due_cents)} testid="partner-detail-due" tone="text-indigo-700" />
        <Kpi label="Projetées" value={m(s.pending_cents)} testid="partner-detail-pending" tone="text-amber-700" />
        <Kpi label="Réglées" value={m(s.paid_cents)} testid="partner-detail-paid" tone="text-emerald-700" />
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map(([kk, lbl]) => <button key={kk} onClick={() => setTab(kk)} data-testid={`partner-tab-${kk}`} className={`whitespace-nowrap px-4 py-2 text-sm font-medium ${tab === kk ? "border-b-2 border-primary text-primary" : "text-slate-500 hover:text-slate-700"}`}>{lbl}</button>)}
      </div>

      {tab === "overview" && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="partner-tab-content-overview">
          <Info icon={User} label="Contact" value={p.contact_name || "—"} />
          <Info icon={Phone} label="Téléphone" value={p.phone || "—"} />
          <Info icon={Mail} label="E-mail" value={p.email || "—"} />
          <div className="rounded-xl border border-slate-200 bg-white p-3 sm:col-span-2 lg:col-span-3"><p className="text-xs text-slate-400">Notes</p><p className="font-medium text-slate-800">{p.notes || "—"}</p></div>
        </div>
      )}

      {tab === "bookings" && (
        <BookingList rows={data.bookings} currency={currency} empty="Aucune réservation liée." />
      )}

      {tab === "commissions" && (
        <BookingList rows={data.bookings.filter((b) => b.commission_amount > 0)} currency={currency} empty="Aucune commission." showCommission />
      )}

      {tab === "settlements" && (
        <div className="space-y-2" data-testid="partner-tab-content-settlements">
          {(data.settlements || []).length === 0 ? <Empty t="Aucun règlement." /> : data.settlements.map((s2) => (
            <button key={s2.id} onClick={() => setDetailSettlement(s2)} data-testid={`settlement-${s2.id}`} className="flex w-full flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 text-left hover:bg-slate-50">
              <div><p className="font-mono text-sm font-medium text-slate-800">{s2.settlement_reference}</p><p className="text-xs text-slate-400">{formatDateShort(s2.settlement_date)} · {PAYMENT_METHODS[s2.payment_method] || s2.payment_method} · {s2.allocations.length} commission(s) · par {s2.created_by_name}</p></div>
              <span className="font-mono font-semibold text-emerald-700">{m(s2.amount_cents)}</span>
            </button>
          ))}
        </div>
      )}

      {tab === "history" && (
        <div className="space-y-1" data-testid="partner-tab-content-history">
          {data.history.length === 0 ? <Empty t="Aucun événement." /> : data.history.map((h, idx) => (
            <div key={idx} className="flex items-center justify-between rounded-lg px-2 py-2 text-sm hover:bg-slate-50" data-testid={`partner-history-${idx}`}>
              <span className="text-slate-700">{PARTNER_ACTION_LABELS[h.action] || h.action}{h.actor_name ? ` · ${h.actor_name}` : ""}</span>
              <span className="text-xs text-slate-400">{formatDateShort(h.created_at)} {formatTime(h.created_at)}</span>
            </div>
          ))}
        </div>
      )}

      <PartnerDialog open={editOpen} onOpenChange={setEditOpen} partner={p} />
      <DeactivateDialog open={statusOpen} onOpenChange={setStatusOpen} partner={p} />
      <SettlementDialog open={settleOpen} onOpenChange={setSettleOpen} partner={p} dueBookings={dueBookings} currency={currency} />
      <SettlementDetailDialog settlement={detailSettlement} open={!!detailSettlement} onOpenChange={(o) => { if (!o) setDetailSettlement(null); }} currency={currency} />
    </div>
  );
}

function BookingList({ rows, currency, empty, showCommission }) {
  if (!rows.length) return <Empty t={empty} />;
  return (
    <div className="space-y-2" data-testid="partner-booking-list">
      {rows.map((b) => {
        const bs = BOOKING_STATUS[b.status] || {};
        const cs = COMMISSION_STATUS[b.commission_status] || null;
        return (
          <Link key={b.id} to={`/reservations?open=${b.id}`} data-testid={`partner-booking-${b.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-white p-3 hover:bg-slate-50">
            <div className="min-w-0">
              <p className="font-mono text-sm font-medium text-slate-800">{b.booking_reference} <span className={`ml-1 rounded-full border px-1.5 py-0.5 text-xs font-medium ${bs.cls || ""}`}>{bs.label || b.status}</span></p>
              <p className="text-xs text-slate-400">{formatDateShort(b.booking_date)} · {b.customer_name} · {b.activity_name}</p>
            </div>
            <div className="text-right">
              {showCommission ? (
                <>
                  <p className="font-mono text-sm font-semibold text-slate-900">{formatMoney(b.commission_amount, currency)}</p>
                  {cs && <span className={`rounded-full border px-1.5 py-0.5 text-xs font-medium ${cs.cls}`} data-testid={`commission-status-${b.id}`}>{cs.label}</span>}
                  {b.commission_status === "DUE" && b.commission_remaining_cents != null && b.commission_remaining_cents < b.commission_amount && <p className="text-xs text-amber-600" data-testid={`commission-remaining-${b.id}`}>reste {formatMoney(b.commission_remaining_cents, currency)}</p>}
                </>
              ) : (
                <p className="font-mono text-sm text-slate-700">{formatMoney(b.total_amount_cents, currency)}</p>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

const Info = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3"><p className="flex items-center gap-1.5 text-xs text-slate-400"><Icon className="h-3.5 w-3.5" /> {label}</p><p className="mt-0.5 font-medium text-slate-800">{value}</p></div>
);
const Kpi = ({ label, value, testid, tone = "text-slate-900" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}><p className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</p><p className={`mt-1.5 text-2xl font-bold ${tone}`}>{value ?? 0}</p></div>
);
const Empty = ({ t }) => <div className="rounded-xl border border-dashed border-slate-300 bg-white py-10 text-center text-sm text-slate-400">{t}</div>;
